import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash, DownloadSimple, Plus, ArrowSquareOut, PaintBrush, CircleNotch } from "@phosphor-icons/react";
import { api, assetUrl, copyToClipboard, errDetail } from "../lib/api";
import { Sidebar } from "../components/Sidebar";
import { ImagePreview } from "../components/ImagePreview";

const TABS = ["Saved Chats", "Gallery", "Prompts"];

export default function Library() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Saved Chats");
  const [saved, setSaved] = useState([]);
  const [assets, setAssets] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [previewAsset, setPreviewAsset] = useState(null);
  const [pushing, setPushing] = useState(null);

  const sendToCanva = async (a) => {
    setPushing(a.id);
    try {
      await api.post("/canva/upload-asset", { asset_id: a.id }, { timeout: 120000 });
      toast.success("Image added to your Canva assets");
    } catch (e) { toast.error(errDetail(e)); } finally { setPushing(null); }
  };

  const load = async () => {
    const [c, a, p] = await Promise.all([api.get("/conversations"), api.get("/assets"), api.get("/prompts")]);
    setSaved(c.data.filter((x) => x.saved));
    setAssets(a.data);
    setPrompts(p.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const addPrompt = async () => {
    if (!title.trim() || !content.trim()) return;
    try {
      await api.post("/prompts", { title, content });
      setTitle(""); setContent(""); setShowAdd(false);
      toast.success("Prompt saved");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const copyPrompt = async (p) => {
    const ok = await copyToClipboard(p.content);
    toast.success(ok ? "Prompt copied — paste it in chat" : "Opening chat");
    navigate("/");
  };

  const inputCls = "w-full bg-[var(--bg)] border border-[var(--border-strong)] rounded-xl px-3.5 py-2.5 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] transition-colors duration-300 placeholder:text-[var(--muted)]";

  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto px-10 py-10">
        <h1 className="font-serif-display font-semibold text-3xl sm:text-4xl tracking-tight mb-8">Library</h1>

        <div className="inline-flex bg-[var(--surface-2)] rounded-full p-1 mb-8">
          {TABS.map((t) => (
            <button key={t} data-testid={`library-tab-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setTab(t)}
              className={`px-5 py-2 text-[13px] font-medium rounded-full transition-colors duration-300 ${tab === t ? "bg-[var(--surface)] text-[var(--text)] border border-[var(--border)]" : "text-[var(--text-2)] hover:text-[var(--text)]"}`}
              style={tab === t ? { boxShadow: "var(--shadow)" } : {}}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Saved Chats" && (
          <div className="space-y-2.5 max-w-2xl" data-testid="saved-chats-list">
            {saved.length === 0 && <p className="text-sm text-[var(--muted)]">No saved chats yet. Bookmark a conversation from the sidebar.</p>}
            {saved.map((c) => (
              <button key={c.id} data-testid={`saved-chat-${c.id}`}
                onClick={() => navigate("/", { state: { openConversation: c.id } })}
                className="w-full flex items-center justify-between bg-[var(--surface)] border border-[var(--border)] rounded-2xl px-5 py-4 hover:border-[var(--accent)]/50 transition-colors duration-300 text-left group">
                <div>
                  <p className="text-sm font-medium text-[var(--text)]">{c.title}</p>
                  <p className="text-xs text-[var(--muted)] mt-1">{new Date(c.updated_at).toLocaleString()}</p>
                </div>
                <ArrowSquareOut size={16} className="text-[var(--muted)] group-hover:text-[var(--accent)] transition-colors duration-300" />
              </button>
            ))}
          </div>
        )}

        {tab === "Gallery" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {assets.length === 0 && <p className="text-sm text-[var(--muted)] col-span-full">No generated images yet. Pick an image model in the chat box.</p>}
            {assets.map((a, i) => (
              <div key={a.id} data-testid={`gallery-item-${a.id}`}
                className={`group relative bg-[var(--surface)] border border-[var(--border)] rounded-2xl overflow-hidden cursor-pointer ${i % 5 === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
                onClick={() => setPreviewAsset(a)}>
                <img src={assetUrl(a.id)} alt={a.prompt} className="w-full h-full object-contain bg-[var(--surface-2)]" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 bg-black/70 backdrop-blur-sm px-4 py-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-xs text-white truncate">{a.prompt}</p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-amber-300">{a.engine}</span>
                    <span className="flex items-center gap-2">
                      <button data-testid={`send-to-canva-${a.id}`} title="Send to Canva assets"
                        onClick={(e) => { e.stopPropagation(); sendToCanva(a); }} disabled={pushing === a.id}
                        className="text-white/70 hover:text-white transition-colors duration-300 disabled:opacity-40">
                        {pushing === a.id ? <CircleNotch size={15} className="animate-spin" /> : <PaintBrush size={15} />}
                      </button>
                      <a href={`${assetUrl(a.id)}${assetUrl(a.id).includes("?") ? "&" : "?"}download=1`} download onClick={(e) => e.stopPropagation()}
                        className="text-white/70 hover:text-white transition-colors duration-300"><DownloadSimple size={15} /></a>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Prompts" && (
          <div className="max-w-2xl">
            <button data-testid="add-prompt-button" onClick={() => setShowAdd(!showAdd)}
              className="flex items-center gap-2 text-[13px] font-medium bg-[var(--surface)] border border-[var(--border-strong)] rounded-full px-4 py-2 mb-6 hover:border-[var(--accent)]/60 transition-colors duration-300">
              <Plus size={14} weight="bold" /> New Prompt Template
            </button>
            {showAdd && (
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-5 mb-6 space-y-3 scale-in" data-testid="add-prompt-form" style={{ boxShadow: "var(--shadow)" }}>
                <input data-testid="prompt-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Prompt title" className={inputCls} />
                <textarea data-testid="prompt-content-input" value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="Prompt content…" className={`${inputCls} resize-none`} />
                <button data-testid="save-prompt-button" onClick={addPrompt}
                  className="rounded-full text-white text-[13px] font-semibold px-5 py-2 transition-colors duration-300" style={{ background: "var(--accent)" }}>Save</button>
              </div>
            )}
            <div className="space-y-2.5" data-testid="prompts-list">
              {prompts.length === 0 && <p className="text-sm text-[var(--muted)]">No prompt templates yet.</p>}
              {prompts.map((p) => (
                <div key={p.id} data-testid={`prompt-item-${p.id}`} className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl px-5 py-4 group">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[var(--text)]">{p.title}</p>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button data-testid={`use-prompt-${p.id}`} onClick={() => copyPrompt(p)} title="Copy & go to chat"
                        className="text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Copy size={15} /></button>
                      <button data-testid={`delete-prompt-${p.id}`} onClick={async () => { await api.delete(`/prompts/${p.id}`); load(); }} title="Delete"
                        className="text-[var(--muted)] hover:text-[var(--error)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg"><Trash size={15} /></button>
                    </div>
                  </div>
                  <p className="text-[13px] text-[var(--text-2)] mt-2 line-clamp-3 whitespace-pre-wrap">{p.content}</p>
                  <p className="text-[11px] text-[var(--muted)] mt-2">by {p.author}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {previewAsset && (
          <ImagePreview url={assetUrl(previewAsset.id)} name={`triad-${previewAsset.id.slice(0, 6)}`} onClose={() => setPreviewAsset(null)} sourceType="asset" sourceId={previewAsset.id} />
        )}
      </main>
    </div>
  );
}
